import { useState, useEffect } from "react";
import { Star } from "lucide-react";
import { useFindAClass } from "../context/FindAClassContext";
import { useBookingLinks } from "../context/BookingLinksContext";
import { useSiteImages } from "../context/SiteImagesContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HeroSection = () => {
  const { openFindAClass } = useFindAClass();
  const bookingLinks = useBookingLinks();
  const siteImages = useSiteImages();
  const [hero, setHero] = useState({ tagline: "Be brave. Be curious. Be confident.", description: "Pre-school gymnastics that gives your little one the best start. Fun, safe classes in Southampton for babies and toddlers.", price_from: "£8.50" });

  useEffect(() => {
    axios.get(`${API}/page/hero`).then(res => setHero(h => ({ ...h, ...res.data }))).catch(() => {});
  }, []);

  return (
    <section className="relative overflow-hidden bg-white">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, #ff3131 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
      
      <div className="container-gymtots relative py-16 md:py-24 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-[#2D3436] leading-[1.1] mb-6 animate-slide-up"
              style={{ fontFamily: 'Fredoka, cursive' }}
              data-testid="hero-title"
            >
              Where Confidence<br />
              <span className="relative inline-block mt-1">
                <span className="text-[#ff3131]">Be</span><span className="text-[#7dbff8]">gins</span>
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 240 12" fill="none" preserveAspectRatio="none">
                  <path d="M2 9C60 3 180 3 238 9" stroke="#ff3131" strokeWidth="3" strokeLinecap="round" opacity="0.3"/>
                </svg>
              </span>
            </h1>

            <p className="text-[#636E72] text-base sm:text-lg mb-3 max-w-md mx-auto lg:mx-0 animate-slide-up stagger-2" style={{ fontFamily: 'Nunito, sans-serif', animationDelay: '0.1s' }}>
              {(hero.tagline || "").split(". ").map((part, i, arr) => (
                <span key={i}>{i > 0 ? ". " : ""}<span className="text-[#ff3131] font-semibold">{part.split(" ")[0]}</span> {part.split(" ").slice(1).join(" ")}</span>
              ))}
            </p>
            <p className="text-[#636E72] text-sm sm:text-base mb-8 max-w-md mx-auto lg:mx-0 animate-slide-up stagger-2" style={{ fontFamily: 'Nunito, sans-serif', animationDelay: '0.15s' }}>
              {hero.description}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start animate-slide-up stagger-3" style={{ animationDelay: '0.2s' }}>
              <button 
                onClick={openFindAClass}
                className="bg-[#ff3131] hover:bg-[#e62e2e] text-white text-base sm:text-lg px-8 py-4 rounded-full font-bold inline-flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg shadow-[#ff3131]/25 cursor-pointer"
                style={{ fontFamily: 'Fredoka, cursive' }}
                data-testid="hero-find-class-btn"
              >
                Find a Class
              </button>
              <a 
                href="/classes" 
                className="border-2 border-[#2D3436]/10 hover:border-[#ff3131] text-[#2D3436] hover:text-[#ff3131] text-base sm:text-lg px-8 py-4 rounded-full font-bold inline-flex items-center justify-center transition-all"
                style={{ fontFamily: 'Fredoka, cursive' }}
                data-testid="hero-cta-secondary"
              >
                View All Classes
              </a>
            </div>
            
            {/* Trust strip */}
            <div className="flex flex-wrap gap-6 justify-center lg:justify-start mt-10 animate-slide-up stagger-4" style={{ animationDelay: '0.3s' }}>
              <div className="flex items-center gap-2">
                <div className="flex -space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-[#ff3131]" fill="#ff3131" />
                  ))}
                </div>
                <span className="text-xs text-[#636E72] font-medium">5-star rated</span>
              </div>
            </div>
          </div>
          
          {/* Right — Hero Image */}
          <div className="relative animate-pop-in stagger-2" style={{ animationDelay: '0.15s' }}>
            <div className="relative max-w-lg mx-auto">
              {/* Main image */}
              <div className="rounded-3xl overflow-hidden shadow-2xl shadow-black/10">
                <img 
                  src={hero.image_url || siteImages["hero-image.jpg"] || ""}
                  alt="Happy toddler doing gymnastics"
                  className="w-full aspect-[4/5] object-cover"
                  data-testid="hero-image"
                />
              </div>
              
              {/* Floating card — Price */}
              <div className="absolute -top-4 -right-4 md:top-6 md:-right-6 bg-white rounded-2xl p-4 shadow-xl border border-gray-50 animate-float">
                <p className="text-xs text-[#636E72] font-medium">Classes from</p>
                <p className="text-2xl font-bold text-[#ff3131]" style={{ fontFamily: 'Fredoka, cursive' }}>{hero.price_from || "£8.50"}</p>
              </div>
              
              {/* Floating card — Ages */}
              <div className="absolute -bottom-4 -left-4 md:bottom-8 md:-left-6 bg-[#ff3131] rounded-2xl p-4 shadow-xl animate-bounce-soft text-white">
                <p className="text-xs font-medium opacity-80">Ages</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Fredoka, cursive' }}>0-7</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
